import CustomSnackbar from '@/screens/main/components/snack-bar';
import {
  registerNotifier,
  type ISnackbarData,
  type TranslationOptions,
} from '@/utils/notification-bridge';
import { shortRequestId } from '@/utils/request-id';
import { datadogRum } from '@datadog/browser-rum';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SnackbarContext } from './snackbar-context';

export const SnackbarProvider: React.FC = (props) => {
  const { children } = props;
  const { t } = useTranslation();

  const [customSnackVisibility, setCustomSnackVisibility] = useState<boolean>(false);
  const [customSnackMessage, setCustomSnackMessage] = useState<string | null>();
  const [customSnackMessageOpts, setCustomSnackMessageOpts] = useState<
    TranslationOptions | undefined
  >();
  const [customSnackDurationInMs, setCustomSnackDurationInMs] = useState<number>(5000);
  const [customSnackIsTimerIcon, setCustomSnackIsTimerIcon] = useState<boolean>(false);
  const [customSnackReferenceId, setCustomSnackReferenceId] = useState<string | undefined>();

  const setCustomSnack = useCallback((args: ISnackbarData) => {
    const { visibility, message, durationInMs, isTimerIcon, messageOpts, referenceId } = args;
    setCustomSnackVisibility(visibility);
    setCustomSnackMessage(message);
    setCustomSnackDurationInMs(durationInMs || 5000);
    setCustomSnackIsTimerIcon(!!isTimerIcon);
    setCustomSnackMessageOpts(messageOpts);
    setCustomSnackReferenceId(referenceId);

    // Split error-link snackbars (carry `referenceId`) from success snackbars so dashboards
    // can filter cleanly on `name = "snackbar.error"`.
    datadogRum.addAction(referenceId ? 'snackbar.error' : 'snackbar', {
      message: message,
      messageOpts: messageOpts,
      referenceId,
    });
  }, []);

  // Let non-React code (e.g. the Apollo error link) push snackbars through `notify(...)`.
  useEffect(() => {
    registerNotifier(setCustomSnack);
    return () => registerNotifier(null);
  }, [setCustomSnack]);

  return (
    <SnackbarContext.Provider
      value={{
        setCustomSnack,
      }}
    >
      {children}
      <CustomSnackbar
        open={customSnackVisibility}
        setOpen={setCustomSnackVisibility}
        message={t(customSnackMessage || '', customSnackMessageOpts)}
        messagePostfix={
          customSnackReferenceId ? ` (#${shortRequestId(customSnackReferenceId)})` : undefined
        }
        durationInMs={customSnackDurationInMs}
        isLight={false}
        closeIcon={true}
        isTimerIcon={customSnackIsTimerIcon}
      />
    </SnackbarContext.Provider>
  );
};

export default SnackbarProvider;
